// ============================================================================
// SYSTEM ANNOUNCEMENT TYPES
// ============================================================================

/**
 * Announcement severity levels.
 * - info: General information, updates
 * - warning: Important notices requiring attention
 * - critical: Urgent issues, service disruptions
 */
export const ANNOUNCEMENT_SEVERITIES = ['info', 'warning', 'critical'] as const;
export type AnnouncementSeverity = (typeof ANNOUNCEMENT_SEVERITIES)[number];

/**
 * Target platforms for announcements.
 * When specified, announcement only shows on those platforms.
 * Empty/null means all platforms.
 */
export const ANNOUNCEMENT_PLATFORMS = ['web', 'ios', 'android'] as const;
export type AnnouncementPlatform = (typeof ANNOUNCEMENT_PLATFORMS)[number];

/**
 * Announcement status (computed, not stored).
 * - draft: No activeFrom date set
 * - scheduled: activeFrom is in the future
 * - active: Currently showing to users
 * - expired: activeUntil is in the past
 */
export const ANNOUNCEMENT_STATUSES = ['draft', 'scheduled', 'active', 'expired'] as const;
export type AnnouncementStatus = (typeof ANNOUNCEMENT_STATUSES)[number];

/**
 * Compute the status of an announcement based on its active dates.
 * Status is derived, not stored.
 */
export function computeAnnouncementStatus(
  activeFrom: Date | null,
  activeUntil: Date | null,
  now: Date = new Date()
): AnnouncementStatus {
  if (!activeFrom) return 'draft';
  if (activeFrom > now) return 'scheduled';
  if (activeUntil && activeUntil < now) return 'expired';
  return 'active';
}

/**
 * Display labels for announcement severities.
 */
export const SEVERITY_LABELS: Record<AnnouncementSeverity, string> = {
  info: 'Info',
  warning: 'Warning',
  critical: 'Critical',
};

/**
 * Display colors for announcement severities (Tailwind-compatible).
 */
export const SEVERITY_COLORS: Record<
  AnnouncementSeverity,
  { bg: string; text: string; border: string }
> = {
  info: {
    bg: 'bg-blue-50',
    text: 'text-blue-800',
    border: 'border-blue-200',
  },
  warning: {
    bg: 'bg-yellow-50',
    text: 'text-yellow-800',
    border: 'border-yellow-200',
  },
  critical: {
    bg: 'bg-red-50',
    text: 'text-red-800',
    border: 'border-red-200',
  },
};

/**
 * Display labels for announcement statuses.
 */
export const STATUS_LABELS: Record<AnnouncementStatus, string> = {
  draft: 'Draft',
  scheduled: 'Scheduled',
  active: 'Active',
  expired: 'Expired',
};

/**
 * Display colors for announcement statuses (Tailwind-compatible).
 */
export const STATUS_COLORS: Record<AnnouncementStatus, { bg: string; text: string }> = {
  draft: { bg: 'bg-gray-100', text: 'text-gray-700' },
  scheduled: { bg: 'bg-purple-100', text: 'text-purple-700' },
  active: { bg: 'bg-green-100', text: 'text-green-700' },
  expired: { bg: 'bg-gray-100', text: 'text-gray-500' },
};

/**
 * Display labels for platforms.
 */
export const PLATFORM_LABELS: Record<AnnouncementPlatform, string> = {
  web: 'Web',
  ios: 'iOS',
  android: 'Android',
};

// ============================================================================
// ANNOUNCEMENT INTERFACES
// ============================================================================

/**
 * Creator info attached to announcements.
 */
export interface AnnouncementCreator {
  id: string;
  email: string;
  name: string | null;
}

/**
 * Full announcement data (admin view).
 */
export interface Announcement {
  id: string;
  severity: AnnouncementSeverity;
  title: string;
  message: string | null;
  linkUrl: string | null;
  linkText: string | null;
  activeFrom: Date | null;
  activeUntil: Date | null;
  dismissible: boolean;
  status: AnnouncementStatus;
  targetPlatforms: AnnouncementPlatform[] | null;
  targetUserIds: string[] | null;
  targetHouseholdIds: string[] | null;
  createdBy: AnnouncementCreator | null;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Active announcement (user view).
 * Minimal data needed to display to users.
 */
export interface ActiveAnnouncement {
  id: string;
  severity: AnnouncementSeverity;
  title: string;
  message: string | null;
  linkUrl: string | null;
  linkText: string | null;
  dismissible: boolean;
}

/**
 * Announcement statistics.
 */
export interface AnnouncementStats {
  totalViews: number;
  dismissals: number;
  dismissalRate: number;
}

/**
 * Paginated announcements response.
 */
export interface PaginatedAnnouncements {
  data: Announcement[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// ============================================================================
// ANNOUNCEMENT DTOs (for API requests)
// ============================================================================

/**
 * DTO for creating an announcement.
 */
export interface CreateAnnouncementDto {
  severity: AnnouncementSeverity;
  title: string;
  message?: string;
  linkUrl?: string;
  linkText?: string;
  activeFrom?: string; // ISO date string
  activeUntil?: string; // ISO date string
  dismissible?: boolean;
  targetPlatforms?: AnnouncementPlatform[];
  targetUserIds?: string[];
  targetHouseholdIds?: string[];
}

/**
 * DTO for updating an announcement.
 */
export interface UpdateAnnouncementDto {
  severity?: AnnouncementSeverity;
  title?: string;
  message?: string;
  linkUrl?: string;
  linkText?: string;
  activeFrom?: string | null; // ISO date string or null to clear
  activeUntil?: string | null; // ISO date string or null to clear
  dismissible?: boolean;
  targetPlatforms?: AnnouncementPlatform[];
  targetUserIds?: string[];
  targetHouseholdIds?: string[];
}

/**
 * Query parameters for listing announcements.
 */
export interface AnnouncementQueryParams {
  status?: AnnouncementStatus;
  severity?: AnnouncementSeverity;
  page?: number;
  limit?: number;
}
